import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { QuantitySelector } from "../components/QuantitySelector";
import { useCart } from "../context/CartContext";
import { formatINR, FREE_SHIPPING_THRESHOLD, SHIPPING_FEE } from "../data/products";

export default function CartPage() {
  const { detailed, subtotal, count, setQty, remove } = useCart();
  const shipping = subtotal === 0 || subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_FEE;

  if (detailed.length === 0) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 bg-ivory px-6 text-center" data-testid="cart-empty">
        <Seo title="Your Bag — Zelvoria" siteName="ZELVORIA" description="Your Zelvoria shopping bag." />
        <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Shopping Bag</p>
        <h1 className="font-serif text-4xl font-light text-ink md:text-5xl">Your bag is empty.</h1>
        <p className="max-w-sm text-sm font-light text-taupe">
          Every ritual begins with a first flame. Discover the collection.
        </p>
        <Link
          to="/candles"
          data-testid="cart-empty-shop-button"
          className="mt-2 bg-cocoa px-10 py-4 text-[11px] uppercase tracking-[0.3em] text-ivory transition-colors duration-500 hover:bg-gold"
        >
          Shop Candles
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-ivory" data-testid="cart-page">
      <Seo title="Your Bag — Zelvoria" siteName="ZELVORIA" description="Review your Zelvoria selections." />
      <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-20">
        <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Your Selections</p>
        <h1 className="mt-3 font-serif text-4xl font-light text-ink md:text-5xl">
          Shopping Bag <span className="text-gold">({count})</span>
        </h1>

        <div className="mt-12 grid gap-12 lg:grid-cols-3">
          <div className="divide-y divide-beige border-y border-beige lg:col-span-2">
            {detailed.map(({ slug, qty, product }) => (
              <div key={slug} className="flex gap-5 py-6 md:gap-8" data-testid={`cart-item-${slug}`}>
                <Link to={`/product/${slug}`} className="h-28 w-24 shrink-0 overflow-hidden bg-cream md:h-36 md:w-28">
                  <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
                </Link>
                <div className="flex flex-1 flex-col">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Link to={`/product/${slug}`} className="font-serif text-xl text-ink transition-colors hover:text-gold">
                        {product.name}
                      </Link>
                      <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-taupe">
                        {product.fragrance} · {product.category}
                      </p>
                      <p className="mt-1 text-xs text-taupe">{formatINR(product.price)} each</p>
                    </div>
                    <button
                      onClick={() => remove(slug)}
                      data-testid={`cart-remove-${slug}`}
                      aria-label={`Remove ${product.name}`}
                      className="text-taupe transition-colors hover:text-gold"
                    >
                      <X size={16} strokeWidth={1.5} />
                    </button>
                  </div>
                  <div className="mt-auto flex items-center justify-between pt-4">
                    <QuantitySelector qty={qty} onChange={(q) => setQty(slug, q)} small />
                    <p className="text-sm font-medium text-ink" data-testid={`cart-line-total-${slug}`}>
                      {formatINR(product.price * qty)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <aside className="h-fit border border-beige bg-parchment p-8 lg:sticky lg:top-32" data-testid="cart-summary">
            <h2 className="font-serif text-2xl font-light text-ink">Order Summary</h2>
            <div className="mt-6 space-y-4 text-sm">
              <div className="flex justify-between">
                <span className="text-taupe">Subtotal</span>
                <span className="font-medium text-ink" data-testid="cart-subtotal">{formatINR(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-taupe">Shipping</span>
                <span className="font-medium text-ink" data-testid="cart-shipping">
                  {shipping === 0 ? "Complimentary" : formatINR(shipping)}
                </span>
              </div>
              {shipping > 0 && (
                <p className="text-xs font-light text-gold">
                  Add {formatINR(FREE_SHIPPING_THRESHOLD - subtotal)} more for complimentary shipping.
                </p>
              )}
              <div className="flex justify-between border-t border-beige pt-4 text-base">
                <span className="uppercase tracking-[0.15em] text-ink">Total</span>
                <span className="font-medium text-ink" data-testid="cart-total">{formatINR(subtotal + shipping)}</span>
              </div>
            </div>
            <Link
              to="/checkout"
              data-testid="proceed-to-checkout-button"
              className="mt-8 flex w-full items-center justify-center gap-3 bg-cocoa py-4 text-[11px] uppercase tracking-[0.3em] text-ivory transition-colors duration-500 hover:bg-gold"
            >
              Proceed to Checkout <ArrowRight size={14} />
            </Link>
            <Link
              to="/candles"
              className="mt-5 block text-center text-[11px] uppercase tracking-[0.25em] text-taupe transition-colors hover:text-gold"
            >
              Continue Shopping
            </Link>
          </aside>
        </div>
      </div>
    </div>
  );
}
